/**
 * Raised curtain masonry on the square tactical grid. Cardinal endpoints and
 * garrison centres stay fixed; battered feet, cut corners and shaded caps give
 * the masonry depth without applying a different projection to the ground.
 */
import type { Graphics } from 'pixi.js';
import { mix, shade } from '../data/palette.ts';
import { WallKind } from '../sim/types.ts';
import type { Vernacular } from './vernacular.ts';
import ashenMaterials from './ashen-materials.json';

/** Shared with the garrison renderer: deck heights, in tiles. */
export const WALL_RISE = 1.05;
export const TOWER_RISE = 1.55;

/** Bit i opens the approach centred on tile i along that tower side. */
export interface TowerConnections { n: number; e: number; s: number; w: number }
export interface WallConnections { n: boolean; e: boolean; s: boolean; w: boolean }

/** Adjacent gate lanes cannot turn a gate away from its masonry abutments. */
export function gateRunVertical(n: number, e: number, s: number, w: number): boolean {
  const masonry = (kind: number): number => kind !== WallKind.None && kind !== WallKind.Gate ? 1 : 0;
  const vertical = masonry(n) + masonry(s), horizontal = masonry(e) + masonry(w);
  if (vertical !== horizontal) return vertical > horizontal;
  return Number(n !== WallKind.None) + Number(s !== WallKind.None) >
    Number(e !== WallKind.None) + Number(w !== WallKind.None);
}

type Point = [number, number];
interface Ink { face: number; side: number; top: number; coping: number; joint: number; foot: number }
const HURT = 0x6b4a44;
// The imported buildings and cardinal ramparts share these stone materials.
// Resolve the JSON swatches once; wall drawing only blends the damage state.
const ASHEN_STONE: Ink = {
  face: Number.parseInt(ashenMaterials.curtain.face, 16),
  side: Number.parseInt(ashenMaterials.curtain.side, 16),
  top: Number.parseInt(ashenMaterials.curtain.top, 16),
  coping: Number.parseInt(ashenMaterials.curtain.coping, 16),
  joint: Number.parseInt(ashenMaterials.curtain.joint, 16),
  foot: Number.parseInt(ashenMaterials.curtain.foot, 16),
};

function inkFor(tier: number, frac: number, v: Vernacular): Ink {
  const base = tier <= 1 ? shade(v.timber, 1.34) : v.wall;
  const lit = tier <= 1 ? shade(v.timber, 1.95) : v.wallLit;
  const stone = v.id === 'ashen' && tier > 1 ? ASHEN_STONE : undefined;
  return {
    face: mix(HURT, stone?.face ?? shade(base, 0.99), frac),
    side: mix(shade(HURT, 0.74), stone?.side ?? shade(base, 0.66), frac),
    top: mix(HURT, stone?.top ?? shade(lit, 1.08), frac),
    coping: mix(HURT, stone?.coping ?? shade(lit, 1.34), frac),
    joint: mix(0x2a1c19, stone?.joint ?? shade(base, 0.59), frac),
    foot: mix(0x241a17, stone?.foot ?? shade(base, 0.48), frac),
  };
}
function polygon(g: Graphics, points: readonly Point[], color: number, alpha = 1): void {
  g.poly(points.flat()).fill({ color, alpha });
}
function line(g: Graphics, a: Point, b: Point, color: number, width: number, alpha = 1): void {
  g.moveTo(a[0], a[1]).lineTo(b[0], b[1]).stroke({ color, width, alpha, cap: 'butt' });
}
function between(a: Point, b: Point, t: number): Point {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}
function raised(points: readonly Point[], height: number): Point[] {
  return points.map(([x, y]) => [x, y - height]);
}
function octagon(x: number, y: number, width: number, depth: number, bevel: number): Point[] {
  const b = Math.min(bevel, width * 0.23, depth * 0.23);
  return [[x + b, y], [x + width - b, y], [x + width, y + b],
    [x + width, y + depth - b], [x + width - b, y + depth],
    [x + b, y + depth], [x, y + depth - b], [x, y + b]];
}

/** Mortar follows the sloping face, including the diagonal corner facets. */
function faceDetail(
  g: Graphics, a: Point, b: Point, footA: Point, footB: Point,
  z: number, tier: number, ink: Ink, frac: number, face: number,
): void {
  if (z < 19) return;
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (length < z * 0.13) return;
  const pen = Math.max(0.55, z * 0.018);
  if (tier <= 1) {
    const count = Math.max(2, Math.round(length / (z * 0.13)));
    for (let i = 1; i < count; i++) {
      const t = i / count;
      line(g, between(a, b, t), between(footA, footB, t), ink.joint, pen);
      if (i % 2) line(g, between(a, b, t + 0.025), between(footA, footB, t + 0.025), ink.top, pen, 0.45);
    }
    line(g, between(a, footA, 0.67), between(b, footB, 0.67), ink.foot, z * 0.055);
  } else {
    polygon(g, [a, b, footB, footA], ink.joint, 0.26);
    const height = (Math.hypot(footA[0] - a[0], footA[1] - a[1]) +
      Math.hypot(footB[0] - b[0], footB[1] - b[1])) / 2;
    // Ashlar at a human scale. The old three enormous courses made a curtain
    // look poured from one smooth slab beside the rendered stone buildings.
    const rows = Math.max(3, Math.round(height / (z * (z < 30 ? 0.25 : 0.17))));
    const blocks = Math.max(1, Math.round(length / (z * (z < 30 ? 0.42 : 0.3))));
    const jointY = Math.min(0.016, z * 0.007 / height);
    const jointX = Math.min(0.04, z * 0.006 / length);
    const point = (u: number, v: number): Point => between(between(a, b, u), between(footA, footB, u), v);
    const courses: number[][][] = [[], [], [], [], []];
    const highlights: Point[][] = [];
    for (let row = 0; row < rows; row++) {
      const top = row / rows + jointY, bottom = (row + 1) / rows - jointY;
      for (let j = 0; j <= blocks; j++) {
        const from = Math.max(0, (j - (row % 2) * 0.5) / blocks) + jointX;
        const to = Math.min(1, (j + 1 - (row % 2) * 0.5) / blocks) - jointX;
        if (to <= from) continue;
        const tone = (row * 7 + j * 13 + row * j * 3) % 5;
        courses[tone].push([point(from, top), point(to, top), point(to, bottom), point(from, bottom)].flat());
        if (z >= 32) highlights.push([point(from, top), point(to, top)]);
      }
    }
    // One fill per stone tone, rather than one Graphics instruction per block.
    // Long curtains are redrawn as the camera moves, so batching matters here.
    for (let tone = 0; tone < courses.length; tone++) {
      if (!courses[tone].length) continue;
      for (const block of courses[tone]) g.poly(block);
      g.fill(mix(face, tone < 2 ? ink.joint : ink.coping, Math.abs(tone - 2) * 0.0825));
    }
    for (let row = 1; row < rows; row++) {
      const start = point(0, row / rows), end = point(1, row / rows);
      g.moveTo(...start).lineTo(...end);
    }
    g.stroke({ color: ink.joint, width: pen * 0.72, alpha: 0.55, cap: 'butt' });
    if (highlights.length) {
      for (const [start, end] of highlights) g.moveTo(...start).lineTo(...end);
      g.stroke({ color: ink.coping, width: pen * 0.7, alpha: 0.28, cap: 'butt' });
    }
    // Rain collects under the coping; damp, darker masonry settles into the
    // ground. These shallow bands follow the face instead of floating on it.
    polygon(g, [point(0, 0), point(1, 0), point(1, 0.055), point(0, 0.055)], ink.foot, 0.22);
    polygon(g, [point(0, 0.82), point(1, 0.85), point(1, 1), point(0, 1)], ink.foot, 0.12);
    if (tier >= 3) line(g, point(0, 0.16), point(1, 0.16), ink.coping, z * 0.026, 0.55);
  }
  if (frac < 0.72) {
    const head = between(a, b, 0.62), foot = between(footA, footB, 0.4);
    const mid = between(head, foot, 0.55);
    line(g, head, [mid[0] - z * 0.035, mid[1]], ink.foot, pen * 1.5);
    line(g, [mid[0] - z * 0.035, mid[1]], foot, ink.foot, pen * 1.3);
  }
}

/** Side faces precede the broad top. A battered base widens the east facet;
 * a colour stripe through an entire shaft cannot accidentally become a roof. */
function prism(
  g: Graphics, footprint: readonly Point[], height: number, ink: Ink,
  batter = 0, z = 0, tier = 3, frac = 1, skip?: (a: Point, b: Point) => boolean,
): void {
  const cx = footprint.reduce((sum, p) => sum + p[0], 0) / footprint.length;
  const cy = footprint.reduce((sum, p) => sum + p[1], 0) / footprint.length;
  const foot = footprint.map(([x, y]): Point => [x + Math.sign(x - cx) * batter, y + Math.sign(y - cy) * batter]);
  const top = raised(footprint, height);
  const edges = footprint.map((_, i) => i).sort((i, j) =>
    (foot[i][1] + foot[(i + 1) % foot.length][1]) - (foot[j][1] + foot[(j + 1) % foot.length][1]));
  for (const i of edges) {
    const next = (i + 1) % footprint.length;
    if (skip?.(footprint[i], footprint[next])) continue;
    const a = top[i], b = top[next], fa = foot[i], fb = foot[next];
    // Back faces are hidden by the top. Leaving them out also keeps the
    // per-frame curtain cheap when a long circuit has hundreds of merlons.
    const faceArea = a[0] * b[1] - b[0] * a[1] + b[0] * fb[1] - fb[0] * b[1] +
      fb[0] * fa[1] - fa[0] * fb[1] + fa[0] * a[1] - a[0] * fa[1];
    if (faceArea >= -0.0001) continue;
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const color = dy > Math.abs(dx) * 0.3 ? ink.side : dx < 0 ? ink.face : ink.top;
    polygon(g, [a, b, fb, fa], color);
    if (z > 0 && (dx < -0.01 || dy > 0.01)) {
      polygon(g, [between(a, fa, 0.94), between(b, fb, 0.94), fb, fa], ink.foot, 0.68);
      faceDetail(g, a, b, fa, fb, z, tier, ink, frac, color);
    }
  }
  polygon(g, top, ink.top);
}

/** Merlons are small solids placed along the roof edge. */
function parapet(
  g: Graphics, outline: readonly Point[], rise: number, z: number, ink: Ink,
  frac: number, tier: number, skip?: (a: Point, b: Point) => boolean, foreground?: Graphics,
): void {
  const top = raised(outline, rise);
  const blocks: Array<{ footprint: Point[]; y: number; height: number; spike: boolean; target: Graphics }> = [];
  for (let edge = 0; edge < top.length; edge++) {
    const next = (edge + 1) % top.length;
    if (skip?.(outline[edge], outline[next])) continue;
    const a = top[edge], b = top[next];
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (length < z * 0.14) continue;
    const ux = (b[0] - a[0]) / length, uy = (b[1] - a[1]) / length;
    const depth = z * (tier <= 1 ? 0.07 : 0.085);
    const inward: Point = [-uy * depth, ux * depth];
    const rail: Point[] = [a, b, [b[0] + inward[0], b[1] + inward[1]], [a[0] + inward[0], a[1] + inward[1]]];
    // Only the near edge goes above the people. The top and the full wall face
    // remain behind them; drawing those again would hide soldiers on the deck.
    const target = foreground && b[0] < a[0] - 0.001 ? foreground : g;
    prism(target, rail, z * (tier <= 1 ? 0.07 : 0.105), { ...ink, top: ink.coping });
    if (z < 16) continue;
    const count = Math.max(1, Math.round(length / (z * (tier <= 1 ? 0.22 : 0.49))));
    const width = length / count * (tier <= 1 ? 0.7 : 0.52);
    for (let i = 0; i < count; i++) {
      if (frac < 0.5 && (i + edge) % 4 === 1) continue;
      const p = between(a, b, (i + 0.5) / count);
      const h = z * (tier <= 1 ? 0.2 : 0.235) * (frac < 0.75 && (i + edge) % 3 === 0 ? 0.65 : 1);
      const footprint: Point[] = [
        [p[0] - ux * width / 2, p[1] - uy * width / 2],
        [p[0] + ux * width / 2, p[1] + uy * width / 2],
        [p[0] + ux * width / 2 + inward[0], p[1] + uy * width / 2 + inward[1]],
        [p[0] - ux * width / 2 + inward[0], p[1] - uy * width / 2 + inward[1]],
      ];
      blocks.push({ footprint, y: p[1], height: h, spike: tier <= 1, target });
    }
  }
  blocks.sort((a, b) => a.y - b.y);
  for (const block of blocks) {
    if (block.spike) {
      const [a, b, c, d] = block.footprint;
      const tip: Point = [(a[0] + c[0]) / 2, (a[1] + c[1]) / 2 - block.height];
      polygon(block.target, [a, b, tip], ink.coping);
      polygon(block.target, [b, c, tip], ink.side);
      polygon(block.target, [c, d, tip], ink.face);
    } else prism(block.target, block.footprint, block.height, { ...ink, top: ink.coping });
  }
}
function thickness(tier: number): number { return tier >= 3 ? 0.94 : tier === 2 ? 0.8 : 0.52; }

function wallPlan(
  sx: number, sy: number, z: number, tier: number, n: boolean, e: boolean, s: boolean, w: boolean,
  inset = 0,
): { outline: Point[]; joins: (a: Point, b: Point) => boolean } {
  const core = z * (thickness(tier) - inset * 2), half = core / 2;
  const cx = sx + z / 2, cy = sy + z / 2;
  const x0 = cx - half, x1 = cx + half, y0 = cy - half, y1 = cy + half;
  const west = sx - 1, east = sx + z + 1, north = sy - 1, south = sy + z + 1;
  const outline: Point[] = [[x0, y0]];
  if (n) outline.push([x0, north], [x1, north]);
  outline.push([x1, y0]);
  if (e) outline.push([east, y0], [east, y1]);
  outline.push([x1, y1]);
  if (s) outline.push([x1, south], [x0, south]);
  outline.push([x0, y1]);
  if (w) outline.push([west, y1], [west, y0]);

  const joins = (a: Point, b: Point): boolean =>
    (n && a[1] === north && b[1] === north) || (s && a[1] === south && b[1] === south) ||
    (w && a[0] === west && b[0] === west) || (e && a[0] === east && b[0] === east);
  // Shared endpoints stay square: beveling them would notch every tile join.
  const bevel = z * (tier >= 3 ? 0.07 : 0.045);
  const cut: Point[] = [];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[(i + outline.length - 1) % outline.length];
    const b = outline[i], c = outline[(i + 1) % outline.length];
    const turn = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (turn > 0 && !joins(a, b) && !joins(b, c)) {
      cut.push(between(b, a, Math.min(0.3, bevel / Math.hypot(b[0] - a[0], b[1] - a[1]))));
      cut.push(between(b, c, Math.min(0.3, bevel / Math.hypot(b[0] - c[0], b[1] - c[1]))));
    } else cut.push(b);
  }
  return { outline: cut, joins };
}

/** Actual raised deck boundary, shared by painting and wall-top picking. */
export function wallTopOutline(
  sx: number, sy: number, z: number, tier: number, n: boolean, e: boolean, s: boolean, w: boolean,
  rise = WALL_RISE,
): Point[] {
  return raised(wallPlan(sx, sy, z, tier, n, e, s, w).outline, z * rise);
}

export function drawWallTile(
  g: Graphics, sx: number, sy: number, z: number, tier: number, frac: number,
  n: boolean, e: boolean, s: boolean, w: boolean, vern: Vernacular, foreground?: Graphics,
  riseTiles = WALL_RISE,
): void {
  const ink = inkFor(tier, frac, vern);
  const { outline, joins } = wallPlan(sx, sy, z, tier, n, e, s, w);
  polygon(g, outline.map(([x, y]) => [x + z * 0.12, y + z * 0.14]), 0x111713, 0.36);
  const rise = z * riseTiles;
  prism(g, outline, rise, ink, z * (tier >= 3 ? 0.085 : tier === 2 ? 0.055 : 0.03), z, tier, frac, joins);
  // The lane crosses every tile join and branches with the wall. It has no
  // cap at a connection, so a long rampart reads as one continuous walkway.
  const lane = wallPlan(sx, sy, z, tier, n, e, s, w, 0.105).outline;
  polygon(g, raised(lane, rise), mix(ink.top, ink.side, 0.19));

  // Flagstone joints mark the usable walkway at the exact garrison height.
  if (z >= 18) {
    const half = z * thickness(tier) / 2, cx = sx + z / 2, cy = sy + z / 2;
    const x0 = cx - half, x1 = cx + half, y0 = cy - half, y1 = cy + half;
    const west = sx - 1, east = sx + z + 1, north = sy - 1, south = sy + z + 1;
    const inset = z * 0.12;
    if (e || w || (!n && !s)) {
      const left = w ? west : x0 + inset, right = e ? east : x1 - inset;
      for (let i = 1; i < 3; i++) {
        const x = left + (right - left) * i / 3;
        line(g, [x, y0 + inset - rise], [x, y1 - inset - rise], ink.joint, z * 0.015, 0.34);
      }
    }
    if (n || s) {
      const back = n ? north : y0 + inset, front = s ? south : y1 - inset;
      for (let i = 1; i < 3; i++) {
        const y = back + (front - back) * i / 3 - rise;
        line(g, [x0 + inset, y], [x1 - inset, y], ink.joint, z * 0.015, 0.34);
      }
    }
  }
  parapet(g, outline, rise, z, ink, frac, tier, joins, foreground);
}

function towerCrown(sx: number, sy: number, z: number, size: number): Point[] {
  return octagon(sx - z * 0.045, sy - z * 0.045, z * size + z * 0.09, z * size + z * 0.09, z * 0.27);
}
export function towerTopOutline(sx: number, sy: number, z: number, size: number): Point[] {
  return raised(towerCrown(sx, sy, z, size), z * TOWER_RISE);
}

/** Split long parapet edges at real approach lanes, leaving the other merlons intact. */
function towerApproaches(
  sx: number, sy: number, z: number, size: number, crown: Point[], connections?: TowerConnections,
): { outline: Point[]; joins: (a: Point, b: Point) => boolean } {
  const lanes: Array<{ axis: 0 | 1; edge: number; from: number; to: number }> = [];
  if (connections) for (let i = 0; i < size; i++) {
    const from = (i + .24) * z, to = (i + .76) * z;
    if (connections.n & (1 << i)) lanes.push({ axis: 0, edge: sy - z * .045, from: sx + from, to: sx + to });
    if (connections.s & (1 << i)) lanes.push({ axis: 0, edge: sy + z * (size + .045), from: sx + from, to: sx + to });
    if (connections.w & (1 << i)) lanes.push({ axis: 1, edge: sx - z * .045, from: sy + from, to: sy + to });
    if (connections.e & (1 << i)) lanes.push({ axis: 1, edge: sx + z * (size + .045), from: sy + from, to: sy + to });
  }
  const onEdge = (a: Point, b: Point, lane: typeof lanes[number]): boolean =>
    Math.abs(a[1 - lane.axis] - lane.edge) < .001 && Math.abs(b[1 - lane.axis] - lane.edge) < .001;
  const outline: Point[] = [];
  for (let i = 0; i < crown.length; i++) {
    const a = crown[i], b = crown[(i + 1) % crown.length], cuts = [0, 1];
    for (const lane of lanes) if (onEdge(a, b, lane)) {
      for (const coordinate of [lane.from, lane.to]) {
        const t = (coordinate - a[lane.axis]) / (b[lane.axis] - a[lane.axis]);
        if (t > 0 && t < 1) cuts.push(t);
      }
    }
    cuts.sort((a, b) => a - b);
    for (let j = 0; j < cuts.length - 1; j++) outline.push(between(a, b, cuts[j]));
  }
  return { outline, joins: (a, b) => lanes.some(lane => onEdge(a, b, lane) &&
    (a[lane.axis] + b[lane.axis]) / 2 > lane.from - .001 &&
    (a[lane.axis] + b[lane.axis]) / 2 < lane.to + .001) };
}

/** The clipped-corner deck covers all four post centres at TOWER_RISE. */
export function drawTower(
  g: Graphics, sx: number, sy: number, z: number, size: number, frac: number, vern: Vernacular, foreground?: Graphics,
  connections?: TowerConnections,
): void {
  const ink = inkFor(3, frac, vern);
  const width = z * size, rise = z * TOWER_RISE;
  const shaft = octagon(sx + z * 0.06, sy + z * 0.06, width - z * 0.12, width - z * 0.12, z * 0.25);
  const crown = towerCrown(sx, sy, z, size);
  polygon(g, crown.map(([x, y]) => [x + z * 0.15, y + z * 0.19]), 0x101712, 0.42);
  prism(g, shaft, rise - z * 0.11, ink, z * 0.08, z, 3, frac);
  prism(g, raised(crown, rise - z * 0.11), z * 0.11, { ...ink, top: ink.coping });
  const floor = octagon(sx + z * 0.14, sy + z * 0.14, width - z * 0.28, width - z * 0.28, z * 0.2);
  polygon(g, raised(floor, rise), mix(ink.top, ink.joint, 0.19));
  if (z >= 16) {
    const inset = z * 0.27, span = width - inset * 2;
    for (let row = 0; row < 3; row++) {
      const y = sy + inset + span * row / 3 - rise;
      if (row > 0) line(g, [sx + inset, y], [sx + width - inset, y], ink.joint, z * 0.015, 0.38);
      for (let i = 1; i < 3; i++) {
        const x = sx + inset + span * (i - (row % 2) * 0.5) / 3;
        line(g, [x, y], [x, y + span / 3], ink.joint, z * 0.015, 0.38);
      }
    }
    for (const offset of [0.29, 0.71]) {
      const x = sx + width * offset, top = sy + width - rise * 0.69;
      const slit = z * 0.055, h = rise * 0.34;
      polygon(g, [[x - slit, top], [x + slit, top], [x + slit, top + h], [x - slit, top + h]], ink.foot);
      line(g, [x - slit * 1.9, top + h * 0.31], [x + slit * 1.9, top + h * 0.31], ink.foot, z * 0.045);
      line(g, [x + slit, top], [x + slit, top + h], ink.coping, z * 0.018, 0.7);
    }
  }
  const approaches = towerApproaches(sx, sy, z, size, crown, connections);
  // Short inset flights show where each lower curtain reaches the higher deck.
  // They are only drawn against an existing neighbour, never across a breach.
  if (connections) for (const side of ['n', 'e', 's', 'w'] as const) for (let i = 0; i < size; i++) {
    if (!(connections[side] & (1 << i))) continue;
    const cx = sx + (i + .5) * z, cy = sy + (i + .5) * z;
    const steps: Point[][] = [];
    for (let step = 0; step < 4; step++) {
      const low = step * .1 * z, high = (step + 1) * .1 * z;
      const lo = z * .25;
      if (side === 'n') steps.push([[cx-lo,sy+low],[cx+lo,sy+low],[cx+lo,sy+high],[cx-lo,sy+high]]);
      if (side === 's') steps.push([[cx-lo,sy+width-high],[cx+lo,sy+width-high],[cx+lo,sy+width-low],[cx-lo,sy+width-low]]);
      if (side === 'w') steps.push([[sx+low,cy-lo],[sx+high,cy-lo],[sx+high,cy+lo],[sx+low,cy+lo]]);
      if (side === 'e') steps.push([[sx+width-high,cy-lo],[sx+width-low,cy-lo],[sx+width-low,cy+lo],[sx+width-high,cy+lo]]);
    }
    // The visible step faces are a small part of the tower shaft, with the
    // landing flush to the curtain and the fourth tread flush to the tower.
    const order = side === 'n' || side === 'w' ? [0,1,2,3] : [3,2,1,0];
    for (const step of order) {
      const bottom = z * (WALL_RISE + (TOWER_RISE - WALL_RISE) * step / 4);
      prism(g, raised(steps[step], bottom), z * (TOWER_RISE - WALL_RISE) / 4,
        { ...ink, top: step % 2 ? ink.top : ink.coping });
    }
  }
  parapet(g, approaches.outline, rise, z, ink, frac, 3, approaches.joins, foreground);
}

function gatePlan(sx: number, sy: number, z: number, vertical: boolean): { piers: Point[][]; bridge: Point[] } {
  const pierWidth = z * 0.25, pierDepth = z * 0.94;
  const piers = vertical
    ? [octagon(sx + z * 0.03, sy - z * 0.025, pierDepth, pierWidth, z * 0.025),
      octagon(sx + z * 0.03, sy + z * 0.775, pierDepth, pierWidth, z * 0.025)]
    : [octagon(sx - z * 0.025, sy + z * 0.03, pierWidth, pierDepth, z * 0.025),
      octagon(sx + z * 0.775, sy + z * 0.03, pierWidth, pierDepth, z * 0.025)];
  const bridge = vertical
    ? [[sx + z * .03, sy - 1], [sx + z * .97, sy - 1],
      [sx + z * .97, sy + z + 1], [sx + z * .03, sy + z + 1]] as Point[]
    : [[sx - 1, sy + z * .03], [sx + z + 1, sy + z * .03],
      [sx + z + 1, sy + z * .97], [sx - 1, sy + z * .97]] as Point[];
  return { piers, bridge };
}
export function gateTopOutline(sx: number, sy: number, z: number, vertical: boolean): Point[] {
  return raised(gatePlan(sx, sy, z, vertical).bridge, z * WALL_RISE);
}

/** Timber leaves swing on the actual passage axis; verticals stay vertical. */
function gateLeaves(g: Graphics, sx: number, sy: number, z: number, vertical: boolean, openness: number, vern: Vernacular): void {
  const angle = Math.max(0, Math.min(1, openness)) * Math.PI * 0.48;
  const reach = z * 0.29, thick = z * 0.04, height = z * 0.76;
  const wood = shade(vern.timber, 1.65);
  const ink: Ink = { face: wood, side: shade(wood, 0.65), top: shade(wood, 1.3), coping: wood, joint: 0x454539, foot: 0x302b23 };
  for (const side of [-1, 1]) {
    const hinge: Point = vertical ? [sx + z * 0.82, sy + z * (side < 0 ? 0.21 : 0.79)]
      : [sx + z * (side < 0 ? 0.21 : 0.79), sy + z * 0.82];
    const dx = vertical ? Math.sin(angle) * reach : -side * Math.cos(angle) * reach;
    const dy = vertical ? -side * Math.cos(angle) * reach : Math.sin(angle) * reach;
    const end: Point = [hinge[0] + dx, hinge[1] + dy];
    const nx = -dy / reach * thick / 2, ny = dx / reach * thick / 2;
    const outline: Point[] = [[hinge[0] - nx, hinge[1] - ny], [end[0] - nx, end[1] - ny], [end[0] + nx, end[1] + ny], [hinge[0] + nx, hinge[1] + ny]];
    // Consistent winding keeps either mirrored leaf a solid even edge-on.
    const area = outline.reduce((sum, p, i) => sum + p[0] * outline[(i + 1) % 4][1] - outline[(i + 1) % 4][0] * p[1], 0);
    if (area < 0) outline.reverse();
    prism(g, outline, height, ink);
    if (z >= 15) {
      for (const t of [0.25, 0.72]) line(g, [hinge[0], hinge[1] - height * t], [end[0], end[1] - height * t], 0x62675b, z * 0.042);
      for (const t of [0.3, 0.66]) {
        const p = between(hinge, end, t);
        line(g, [p[0], p[1] - height * 0.02], [p[0], p[1] - height * 0.98], ink.foot, z * 0.012, 0.7);
      }
    }
  }
}

/** Piers flank the passage along the wall run, not across the opening. */
export function drawGatehouse(
  g: Graphics, sx: number, sy: number, z: number, vertical: boolean, frac: number, vern: Vernacular, foreground?: Graphics, open = 0,
  connections?: WallConnections,
): void {
  const ink = inkFor(3, frac, vern);
  const deck = z * WALL_RISE;
  const { piers, bridge } = gatePlan(sx, sy, z, vertical);
  for (const pier of piers) polygon(g, pier.map(([x, y]) => [x + z * 0.1, y + z * 0.15]), 0x101611, 0.38);
  // A paved threshold stays visible through the opening. It has no painted
  // floor-door that would still look shut after the real leaves have opened.
  const threshold = vertical ? octagon(sx - z * 0.04, sy + z * 0.22, z * 1.08, z * 0.56, z * 0.03)
    : octagon(sx + z * 0.22, sy - z * 0.04, z * 0.56, z * 1.08, z * 0.03);
  polygon(g, threshold, mix(ink.top, 0x998d6c, 0.5));
  polygon(g, threshold.map(([x, y]) => [x + z * 0.02, y + z * 0.01]), 0x18231e, 0.14);
  gateLeaves(g, sx, sy, z, vertical, open, vern);
  for (const pier of piers) prism(g, pier, deck - z * .16, ink, z * 0.025, z, 3, frac);
  // One lintel/deck meets both curtain ends at exactly their walking height.
  // The piers no longer have separate tall caps obstructing that route.
  prism(g, raised(bridge, deck - z * .16), z * .16, { ...ink, top: ink.coping });
  const links = connections ?? { n: vertical, e: !vertical, s: vertical, w: !vertical };
  const railEdges = (a: Point, b: Point): boolean => vertical ?
    (links.n && a[1] === sy - 1 && b[1] === sy - 1) ||
      (links.s && a[1] === sy + z + 1 && b[1] === sy + z + 1) :
    (links.w && a[0] === sx - 1 && b[0] === sx - 1) ||
      (links.e && a[0] === sx + z + 1 && b[0] === sx + z + 1);
  const floor: Point[] = vertical ?
    [[sx + z * .14, sy - 1], [sx + z * .86, sy - 1],
      [sx + z * .86, sy + z + 1], [sx + z * .14, sy + z + 1]] :
    [[sx - 1, sy + z * .14], [sx + z + 1, sy + z * .14],
      [sx + z + 1, sy + z * .86], [sx - 1, sy + z * .86]];
  polygon(g, raised(floor, deck), mix(ink.top, ink.side, 0.19));
  parapet(g, bridge, deck, z, ink, frac, 3, railEdges, foreground);
}
